import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '../../../../shared/di/container.js';

export interface UseNotificationListParams {
  projectId?: string;
  unreadOnly?: boolean;
  type?: string;
  limit?: number;
  offset?: number;
  search?: string;
}

export function useNotificationList(params: UseNotificationListParams = {}) {
  const { search, projectId, unreadOnly, type, limit = 20, offset = 0 } = params;
  const trimmed = search?.trim();
  return useQuery({
    queryKey: ['notifications', 'list', { projectId, unreadOnly, type, limit, offset, search: trimmed ?? '' }],
    queryFn: () =>
      trimmed
        ? container.notification.searchNotificationsUseCase({ query: trimmed, limit, offset })
        : container.notification.listNotificationsUseCase({ projectId, unreadOnly, type, limit, offset }),
  });
}

export function useUnreadCount(projectId?: string) {
  return useQuery({
    queryKey: ['notifications', 'unread', projectId ?? null],
    queryFn: () => container.notification.getUnreadCountUseCase(projectId),
    refetchOnWindowFocus: true,
  });
}

export function useNotificationMutations() {
  const queryClient = useQueryClient();
  const inv = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation({
    mutationFn: (id: string) => container.notification.markNotificationReadUseCase(id),
    onSuccess: inv,
  });

  const markAllRead = useMutation({
    mutationFn: (projectId?: string) => container.notification.markAllNotificationsReadUseCase(projectId),
    onSuccess: inv,
  });

  const deleteRead = useMutation({
    mutationFn: () => container.notification.deleteReadNotificationsUseCase(),
    onSuccess: inv,
  });

  return { markRead, markAllRead, deleteRead };
}

export function useNotificationRealtime(userId?: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return;
    const unsub = container.notification.subscribeNotifications(userId, () => {
      // Invalidate queries when a change occurs
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });
    return () => {
      unsub();
    };
  }, [userId, queryClient]);
}
